import { useMemo } from "react";
import {
  useChatReferences_AdminLibraryItemsQuery,
  useChatReferences_TraineeLibraryItemsQuery,
  useChatReferences_LegacyAdminLibraryBySearchDocsQuery,
  useChatReferences_LegacyAssignmentsBySearchDocsQuery,
  type LibraryItemForChatReferenceFragment,
} from "./useChatReferences.generated";
import { useIsTrainee } from "../../app/hooks/useIsTrainee";
import useUser from "../../app/hooks/useUser";
import { gql } from "@apollo/client";

export type ChatReferenceLibraryItem = LibraryItemForChatReferenceFragment;

export interface ChatReference {
  // Either the library item id (pgvector annotations) or the search document
  // id parsed from the legacy `<uuid>.txt` filename (OpenAI vector-store
  // annotations from older chats). MessageAnnotations builds its lookup key
  // the same way and reads this map back.
  lookupId: string;
  libraryItem: ChatReferenceLibraryItem;
}

export const useChatReferences = (
  libraryItemIds: string[],
  searchDocumentIds: string[] = [],
): {
  references: ChatReference[];
  loading: boolean;
  error?: Error;
} => {
  const { isTrainee } = useIsTrainee();
  const { user } = useUser();
  const userId = user?.id;

  // Sort the inputs so apollo's cache key stays stable across re-renders that
  // produce the same set of ids.
  const sortedLibIds = useMemo(
    () => [...libraryItemIds].sort(),
    [libraryItemIds],
  );
  const sortedSearchDocIds = useMemo(
    () => [...searchDocumentIds].sort(),
    [searchDocumentIds],
  );

  const hasLibIds = sortedLibIds.length > 0;
  const hasSearchDocIds = sortedSearchDocIds.length > 0;

  // Channel A — pgvector citations (modern flow). The trainee query mirrors
  // the access model Ask Opus uses on the backend so folder/automation/blast
  // access resolves; `myAssignment` carries the deep-link nav data.
  const {
    data: traineeLibData,
    loading: traineeLibLoading,
    error: traineeLibError,
  } = useChatReferences_TraineeLibraryItemsQuery({
    skip: !hasLibIds || !isTrainee || !userId,
    variables: {
      input: {
        filters: {
          userId: userId ?? -1,
          ids: { value: sortedLibIds },
        },
      },
    },
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
  });

  const {
    data: adminLibData,
    loading: adminLibLoading,
    error: adminLibError,
  } = useChatReferences_AdminLibraryItemsQuery({
    skip: !hasLibIds || isTrainee,
    variables: {
      input: {
        filters: {
          ids: { value: sortedLibIds },
        },
      },
    },
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
  });

  // Channel B — legacy OpenAI vector-store citations. These predate pgvector
  // and arrive without `metadata.library_item_id`, so the only way to resolve
  // them is by the search document id parsed from the filename. New chats
  // never go through this path; chat history rendered for an existing user
  // can. `TraineeLibrary` doesn't accept `searchDocumentIds`, so trainees
  // fall back to `Assignments`.
  const {
    data: legacyTraineeData,
    loading: legacyTraineeLoading,
    error: legacyTraineeError,
  } = useChatReferences_LegacyAssignmentsBySearchDocsQuery({
    skip: !hasSearchDocIds || !isTrainee,
    variables: {
      input: {
        filters: {
          searchDocumentIds: { value: sortedSearchDocIds },
          includeCompletedAssignmentsWithoutAccessSource: { value: true },
        },
      },
    },
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
  });

  const {
    data: legacyAdminData,
    loading: legacyAdminLoading,
    error: legacyAdminError,
  } = useChatReferences_LegacyAdminLibraryBySearchDocsQuery({
    skip: !hasSearchDocIds || isTrainee,
    variables: {
      input: {
        filters: {
          searchDocumentIds: { value: sortedSearchDocIds },
        },
      },
    },
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
  });

  const references = useMemo<ChatReference[]>(() => {
    const items: ChatReference[] = [];

    // Channel A — keyed by library item id.
    const libObjects: ChatReferenceLibraryItem[] = isTrainee
      ? traineeLibData?.TraineeLibrary.objects ?? []
      : adminLibData?.AdminLibrary.objects ?? [];
    libObjects.forEach((libraryItem) => {
      if (sortedLibIds.includes(libraryItem.id)) {
        items.push({ lookupId: libraryItem.id, libraryItem });
      }
    });

    // Channel B — keyed by search document id. One library item may expose
    // multiple search documents; we emit one reference per matching id so
    // MessageAnnotations can look up each citation independently.
    if (isTrainee) {
      legacyTraineeData?.Assignments.objects?.forEach((assignment) => {
        assignment.libraryItem.searchDocuments?.forEach((searchDoc) => {
          if (sortedSearchDocIds.includes(searchDoc.id)) {
            items.push({
              lookupId: searchDoc.id,
              libraryItem: assignment.libraryItem,
            });
          }
        });
      });
    } else {
      legacyAdminData?.AdminLibrary.objects?.forEach((libraryItem) => {
        libraryItem.searchDocuments?.forEach((searchDoc) => {
          if (sortedSearchDocIds.includes(searchDoc.id)) {
            items.push({ lookupId: searchDoc.id, libraryItem });
          }
        });
      });
    }

    return items;
  }, [
    isTrainee,
    sortedLibIds,
    sortedSearchDocIds,
    traineeLibData,
    adminLibData,
    legacyTraineeData,
    legacyAdminData,
  ]);

  const loading = isTrainee
    ? traineeLibLoading || legacyTraineeLoading
    : adminLibLoading || legacyAdminLoading;
  const error = isTrainee
    ? traineeLibError ?? legacyTraineeError
    : adminLibError ?? legacyAdminError;

  return useMemo(
    () => ({ references, loading, error }),
    [references, loading, error],
  );
};

gql`
  fragment LibraryItemForChatReference on LibraryItem {
    id
    type
    name {
      uuid
      myTranslation
    }
    coverImage {
      id
      emojiIcon
      background
      imageUrls {
        id
        original
        wide
        medium
        thumb
      }
    }
    # Needed for the legacy OpenAI vector-store path so we can match citations
    # by search document id when the annotation lacks metadata.library_item_id.
    searchDocuments {
      id
      language
    }
    # Populated for the current user via prefetch; provides
    # currentCourseInstance / currentModuleInstance for deep-link navigation.
    myAssignment {
      id
      currentCourseInstance {
        id
      }
      currentModuleInstance {
        id
      }
    }
  }

  query ChatReferences_AdminLibraryItems($input: AdminLibraryInput!) {
    AdminLibrary(input: $input, pagination: { limit: 15, offset: 0 }) {
      objects {
        ...LibraryItemForChatReference
      }
      totalCount
    }
  }

  query ChatReferences_TraineeLibraryItems($input: TraineeLibraryInput!) {
    TraineeLibrary(input: $input, pagination: { limit: 15, offset: 0 }) {
      objects {
        ...LibraryItemForChatReference
      }
      totalCount
    }
  }

  # Legacy lookup by search document id. Used only when chat history contains
  # OpenAI vector-store annotations that predate the pgvector rollout.
  query ChatReferences_LegacyAdminLibraryBySearchDocs(
    $input: AdminLibraryInput!
  ) {
    AdminLibrary(input: $input, pagination: { limit: 15, offset: 0 }) {
      objects {
        ...LibraryItemForChatReference
      }
      totalCount
    }
  }

  query ChatReferences_LegacyAssignmentsBySearchDocs(
    $input: AssignmentsInput!
  ) {
    Assignments(input: $input, pagination: { limit: 15, offset: 0 }) {
      objects {
        id
        libraryItem {
          ...LibraryItemForChatReference
        }
      }
      totalCount
    }
  }
`;
